/**
 * 広告同意管理ユーティリティ
 *
 * - ログイン済み: varuna DB（ad_consent_flags）で管理 → クロスデバイス同期
 * - 非ログイン: Cookie にフォールバック（端末ごと）
 *
 * Cookie キー形式: _YAGISH_AD_CONSENT_{NETWORK_ID}
 * DB 形式: ad_consent_flags = '{"meta":"1","tiktok":"0"}'
 */

import { AD_NETWORKS, getNetworkById, AD_FIRING_ENABLED } from '@/config/ad-networks'

// ── Cookie ───────────────────────────────────────────────────────────

function cookieKey (networkId) {
  return `_YAGISH_AD_CONSENT_${networkId.toUpperCase()}`
}

function getCookie (key) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + encodeURIComponent(key) + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie (key, value) {
  const expires = new Date()
  expires.setFullYear(expires.getFullYear() + 1)
  document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
}

// ── ローカル判定（Cookie） ───────────────────────────────────────────

/** Cookie から同意フラグを取得（'1' | '0' | null） */
export function getConsentFlag (networkId) {
  return getCookie(cookieKey(networkId))
}

/** Cookie に同意フラグを保存 */
export function setConsentFlag (networkId, value) {
  setCookie(cookieKey(networkId), value)
}

/** 同意済みかどうか */
export function hasConsent (networkId) {
  return getConsentFlag(networkId) === '1'
}

/** 同意・非同意いずれかを選択済みか */
export function isConsentDecided (networkId) {
  return getConsentFlag(networkId) !== null
}

/** 未決定のネットワーク一覧を返す */
export function getUndecidedNetworks () {
  return AD_NETWORKS.filter(n => !isConsentDecided(n.id))
}

// ── varuna API 連携 ──────────────────────────────────────────────────

/**
 * varuna から全ネットワークの同意フラグを取得し Cookie に同期する。
 * @returns {Promise<object>} { meta: '1', tiktok: '0', ... }
 */
export async function fetchAllFlagsFromServer (axios, varuna, uuid) {
  try {
    const formData = new FormData()
    formData.append('uuid', uuid)
    const res = await axios.post(varuna.endPoints.getAdConsentFlags, formData, { timeout: 10000 })
    const flags = res.data.flags || {}
    // Cookie に同期
    for (const [id, val] of Object.entries(flags)) {
      if (val === '1' || val === '0') {
        setCookie(cookieKey(id), val)
      }
    }
    return flags
  } catch (e) {
    return {}
  }
}

/**
 * 特定ネットワークの同意フラグを varuna と Cookie 両方に保存する。
 * @param {string} networkId
 * @param {boolean} value
 */
export async function saveConsentFlagToServer (axios, varuna, uuid, networkId, value) {
  setConsentFlag(networkId, value ? '1' : '0')
  try {
    const formData = new FormData()
    formData.append('uuid', uuid)
    formData.append('network_id', networkId)
    formData.append('consent_value', value ? '1' : '0')
    await axios.post(varuna.endPoints.updateAdConsentFlag, formData, { timeout: 10000 })
  } catch (e) {
    // サーバー保存失敗は Cookie のみで継続（サイレント）
  }
}

// ── タグ発火 ─────────────────────────────────────────────────────────

/** 指定ネットワークのタグを発火（発火が有効かつ同意済みの場合のみ） */
export function fireNetwork (networkId) {
  if (!AD_FIRING_ENABLED) return
  if (!hasConsent(networkId)) return
  const network = getNetworkById(networkId)
  if (network && typeof network.fire === 'function') network.fire()
}

/** 指定ネットワークの Lead CV イベントを発火（発火が有効かつ同意済みの場合のみ） */
export function fireLeadEvent (networkId) {
  if (!AD_FIRING_ENABLED) return
  if (!hasConsent(networkId)) return
  const network = getNetworkById(networkId)
  if (network && typeof network.fireLead === 'function') network.fireLead()
}

/** 同意済み全ネットワークのタグを一括発火（ページ遷移時など） */
export function fireAllConsented () {
  for (const network of AD_NETWORKS) {
    fireNetwork(network.id)
  }
}

/** 同意済み全ネットワークの Lead CV イベントを一括発火 */
export function fireAllLeadEvents () {
  for (const network of AD_NETWORKS) {
    fireLeadEvent(network.id)
  }
}
